import { useState } from 'react';

import { ChevronsUpDown, Building2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { useGetGames } from '@/api/getGames';
import { useMyPermissions } from '@/api/getMyPermissions';
import { useGetStudios } from '@/api/getStudios';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { administersStudio } from '@/models/permission';

/**
 * The current Studio, always shown, with a switch to every Studio the viewer can
 * reach. Picking one opens its dashboard when the viewer administers it, else its
 * first reachable Game — a non-administrator has no studio page to land on.
 */
export const StudioBreadcrumb = ({ studioCode }: { studioCode: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { data: studiosData } = useGetStudios();
  const { data: gamesData } = useGetGames();
  const { data: me } = useMyPermissions();

  const roles = me?.roles ?? [];
  const studios = studiosData?.studios ?? [];
  const name =
    studios.find(({ code }) => code === studioCode)?.name ?? studioCode;
  const administersCurrent = administersStudio(roles, studioCode);

  const landingOf = (code: string) => {
    if (administersStudio(roles, code)) return `/studios/${code}`;
    const game = gamesData?.games.find(
      ({ studio_code }) => studio_code === code,
    );
    return game ? `/studios/${code}/${game.game_code}` : '/studios';
  };

  const switchStudio = (code: string) => {
    setIsOpen(false);
    if (code !== studioCode) navigate(landingOf(code));
  };

  const label = administersCurrent ? (
    <Link
      to={`/studios/${studioCode}`}
      className="truncate hover:text-foreground transition-colors"
    >
      {name}
    </Link>
  ) : (
    <span className="truncate">{name}</span>
  );

  // Nothing to switch to, and no studio page to list them from either.
  if (studios.length < 2 && !administersCurrent) return label;

  return (
    <>
      {label}
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-6">
            <ChevronsUpDown />
            <span className="sr-only">Switch studio</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-xs">
          <DropdownMenuRadioGroup
            value={studioCode}
            onValueChange={switchStudio}
            className="max-h-48 overflow-y-auto"
          >
            {studios.map((studio) => (
              <DropdownMenuRadioItem key={studio.code} value={studio.code}>
                <span className="truncate">{studio.name}</span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/studios">
              <Building2 className="size-4" />
              <span>All studios</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
